// API calls are from the user_view.py file

import { AxiosError } from "axios"
import AxiosInstance from "../components/Axios"
import { EmployeeNameEmail, EmployeeCreationProps, EmployeeProps } from "../interfaces/employee_type"
import { OnBoardingFormData } from "../interfaces/onboarding_types"
import {API, APIResponse} from "@/components/_Shared/API/API.ts";

// The object keys in which error might show up
export type EmployeeResponse = {
    username: string
    password: string
    email: string
}
/**
 * Employee Creation API
 * 
 * Sends a POST request to the backend and creates an employee
 */
export async function createEmployee(user : EmployeeCreationProps): Promise<APIResponse<EmployeeResponse>> {
   return API.post<EmployeeResponse>("auth/register/", user)
}

export async function updateEmployee(user : EmployeeProps): Promise<number> {
    try {
        const response = await AxiosInstance.put('api/user/id/' + user.id, user)

        if (response.status === 200) {
            return response.status
        } else {
            throw new Error('Error updating employee')
        }
    } catch (error) {
        console.error("Server Error: ",error)
        throw error
    }
}

export async function deleteEmployee(id: string): Promise<number> {
    try {
        const response = await AxiosInstance.delete('api/user/delete_user/' + id)

        if (response.status === 204) {
            return response.status
        } else {
            throw new Error('Error deleting employee')
        }
    } catch (error) {
        console.error("Server Error: ",error)
        throw error
    }
}

/**
 * @returns a list of ONLY employee names
 */
export async function getEmployeeNameList(): Promise<string[]> {
    try {
        const response = await AxiosInstance.get('api/user/all_users_names')


        if (response.status === 200) {
            const names: string[] = response.data.map((employee: { name: string }) => employee.name)
            return names
        } else {
            throw new Error('Error getting employee list')
        }

    } catch (error) {
        console.error("Server Error: ",error)
        throw error
    }
}

/** Have this serach for one employee by it's primary key (id)
 */
export async function getEmployeeDataById(id: string): Promise<EmployeeProps
> {
    try {
        const response = await AxiosInstance.get('api/user/id/' + id)

        if (response.status === 200) {
            return response.data
        } else {
            throw new Error('Error getting employee')
        }

    } catch (error) {
        console.error("Server Error: ",error)
        throw error
    }
}

/**
 * Send a GET request to the backend and get all employees
 * 
 */
export async function getAllEmployeeData(): Promise<EmployeeProps[]>  {
    try {
        const response = await AxiosInstance.get('api/user/all_users')

        if (response.status === 200) {
            return response.data
        } else {
            throw new Error('Error getting employee list')
        }

    } catch (error) {
        console.error("Server Error: ",error)
        throw error
    }
}

/**
 * Get all employees and return a list of names and emails
 * 
 * @param for_selection - If true, return a list of names and emails for selection (used for dropdowns) Temporary as I need to update the other functions that use it. This isn't used for the API call
 * @returns A list of names and emails
 */
export async function getAllEmployeeNameAndEmail({for_selection = false, filter_by_hierarchy = ''}: {for_selection?: boolean, filter_by_hierarchy?: string} = {}): Promise<EmployeeNameEmail[]> {
    try {
        const response = await AxiosInstance.get(
          "api/user/all_users_name_and_email",
          {params: {filter_by_hierarchy}}
        )

        if (for_selection) {
            return response.data.map((employee: { name: string, email: string }) => ({value: employee.email, label: employee.name}))
        } else {
            return response.data
        }
    } catch (error) {
        if (error instanceof AxiosError) {
            if (error.response?.status === 400) {
                // 400 Bad Request
                throw new Error('Error getting employee list')
            } else if (error.response?.status === 403) {
                // 403 Forbidden
                throw new Error('You do not have permission to perform this action')
            }
        } else if (error instanceof Error) {
            console.error("Server Error: ", error)
            throw error
        }

        throw new Error('Error getting employee list')
    }
}

export async function postOnBoardingData(data: OnBoardingFormData): Promise<number> {
    try {
        const response = await AxiosInstance.post('api/user/onboarding', data)
        return response.status
    } catch (error) {
        console.error("Server Error: ",error)
        throw error
    }
}

export async function resetPasswordEmployee(id: string): Promise<number> {
    try {
        const response = await AxiosInstance.patch('api/user/reset_password/' + id)
        return response.status
    } catch (error) {
        console.error("Server Error: ",error)
        throw error
    }
}
